import { useState, useEffect, useRef } from 'react'
import { Plus, Search, Image, Video, FileText, Music, CheckCircle, Upload, X } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../config/api'
import { toast } from './Toast'
import { Button, Spinner, Input, Modal, ModalHeader, ModalBody, ModalFooter } from './ui'

interface MediaItem {
  id: number
  title: string
  type: string
  file_url: string
  file_name: string
  category_name?: string
  category_color?: string
}

interface MediaPickerModalProps {
  open: boolean
  onClose: () => void
  selectedIds: number[]
  onConfirm: (ids: number[]) => void
  multiple?: boolean
}

const typeIcons: Record<string, React.ElementType> = { image: Image, video: Video, audio: Music, document: FileText }
const typeLabels: Record<string, string> = { image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Documento' }

export default function MediaPickerModal({ open, onClose, selectedIds, onConfirm, multiple = true }: MediaPickerModalProps) {
  const [loading, setLoading] = useState(true)
  const [media, setMedia] = useState<MediaItem[]>([])
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  // Upload inline
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSelected([...selectedIds])
      setSearch('')
      setTypeFilter('')
      setShowUpload(false)
      setUploadFile(null)
      setUploadTitle('')
      fetchMedia()
    }
  }, [open])

  async function fetchMedia() {
    setLoading(true)
    try {
      const params: any = { per_page: 100 }
      const res = await axios.get(buildApiUrl('/v1/community/media'), { params })
      setMedia(res.data.data)
    } catch {} finally { setLoading(false) }
  }

  function toggleSelect(id: number) {
    if (multiple) {
      setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    } else {
      setSelected([id])
    }
  }

  async function handleUpload() {
    if (!uploadFile || !uploadTitle.trim()) return
    setUploading(true)
    setUploadProgress(0)
    try {
      const fd = new FormData()
      fd.append('file', uploadFile)
      fd.append('title', uploadTitle)
      const res = await axios.post(buildApiUrl('/v1/community/media'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
        },
        timeout: 600000, // 10 minutos para uploads grandes
      })
      const newItem = res.data.data
      setMedia(prev => [newItem, ...prev])
      setSelected(prev => [...prev, newItem.id])
      toast('success', 'Mídia importada')
      setShowUpload(false)
      setUploadFile(null)
      setUploadTitle('')
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao importar')
    } finally { setUploading(false); setUploadProgress(0) }
  }

  const filtered = media.filter(m => {
    if (typeFilter && m.type !== typeFilter) return false
    if (search && !m.title.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <ModalHeader onClose={onClose}>
        Biblioteca de Mídias
        {selected.length > 0 && (
          <span className="ml-2 text-sm font-normal text-brand-600">{selected.length} selecionada(s)</span>
        )}
      </ModalHeader>
      <ModalBody className="space-y-4">
        {/* Busca + Filtros + Importar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar mídia..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-brand-300 focus:ring-2 focus:ring-brand-500/10 outline-none"
            />
          </div>
          <div className="flex gap-1">
            {['', 'image', 'video', 'audio', 'document'].map(t => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${typeFilter === t ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {t ? typeLabels[t] : 'Todos'}
              </button>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowUpload(!showUpload)}>
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Importar
          </Button>
        </div>

        {/* Upload inline */}
        {showUpload && (
          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
            <input ref={fileRef} type="file" className="hidden" onChange={e => {
              const f = e.target.files?.[0]
              if (f) { setUploadFile(f); if (!uploadTitle) setUploadTitle(f.name.replace(/\.[^.]+$/, '')) }
            }} />

            {uploadFile ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                  {(() => { const I = typeIcons[uploadFile.type?.split('/')[0]] || FileText; return <I className="w-5 h-5 text-brand-500" /> })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500">{(uploadFile.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
                <button type="button" onClick={() => { setUploadFile(null); if (fileRef.current) fileRef.current.value = '' }} className="text-xs text-gray-500 hover:text-gray-700">Trocar</button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-400 hover:bg-white transition-colors">
                <Upload className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Selecionar arquivo</span>
              </button>
            )}

            {uploading ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Enviando...</span>
                  <span className="text-xs font-medium text-brand-600">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                {uploadProgress === 100 && <p className="text-xs text-gray-400">Processando no servidor...</p>}
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Título da mídia *" />
                </div>
                <Button type="button" size="sm" onClick={handleUpload} disabled={!uploadFile || !uploadTitle.trim()}>
                  Importar
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Grid de mídias */}
        {loading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">
            {search || typeFilter ? 'Nenhuma mídia encontrada com esses filtros' : 'Nenhuma mídia na biblioteca. Importe acima.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto p-1">
            {filtered.map(m => {
              const isSelected = selected.includes(m.id)
              const TypeIcon = typeIcons[m.type] || FileText
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleSelect(m.id)}
                  className={`relative flex flex-col items-center p-2 rounded-xl border transition-all text-center ${
                    isSelected ? 'border-brand-400 bg-brand-25 ring-1 ring-brand-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Thumb */}
                  {m.type === 'image' ? (
                    <img src={m.file_url} alt="" className="w-full h-20 rounded-lg object-cover mb-2" />
                  ) : (
                    <div className="w-full h-20 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                      <TypeIcon className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  <p className="text-xs font-medium text-gray-800 truncate w-full">{m.title}</p>
                  <p className="text-[10px] text-gray-400">{typeLabels[m.type] || m.type}</p>

                  {/* Check */}
                  {isSelected && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                      <CheckCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => { onConfirm(selected); onClose() }}>
          Confirmar {selected.length > 0 ? `(${selected.length})` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
